import reduxStore from "../../configureEnvStore";
import { GLOBAL_MEETING_DATA } from "../../constants/PublicVariables";
import { setOfflineModeUploadedImageCount } from "../../reducers/offline-mode-reducer";
import { LogFirebase } from "../../services/firestore";

class IndexedDBService {
    constructor() {
        this.dbName = "offline-mode-Photos-database";
        this.version = 1;
        this.storeName = "offline-mode-Photos-store";
        this.db = null;
        this.oldSessionCleaned = false; // flag to avoid repeat deletion
    }

    async init() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version);

            request.onerror = (error) => {
                LogFirebase("OfflineMode", "indexed_DB_open_error", {
                    error: error?.target?.error?.message || "Unknown error",
                });
                reject(new Error("Failed to open IndexedDB"));
            };

            request.onsuccess = (event) => {
                LogFirebase("OfflineMode", "indexed_DB_open_success", {
                    dbName: this.dbName,
                    version: this.version,
                });
                this.db = event.target.result;
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                if (!db.objectStoreNames.contains(this.storeName)) {
                    const store = db.createObjectStore(this.storeName, { keyPath: "id", autoIncrement: true });
                    store.createIndex("sessionId", "sessionId", { unique: false });
                    LogFirebase("OfflineMode", "indexed_DB_store_created", {
                        storeName: this.storeName,
                        dbName: this.dbName,
                        version: this.version,
                    });
                }
            };
        });
    }

    async ensureDB() {
        if (!this.db) {
            await this.init();
        }
        return this.db;
    }

    async storeImage(imageData) {
        const db = await this.ensureDB();

        // Only once per session, delete old session images
        if (!this.oldSessionCleaned) {
            await this.deleteImagesFromOtherSessions(imageData.sessionId);
            this.oldSessionCleaned = true;
        }

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.storeName, "readwrite");
            const store = transaction.objectStore(this.storeName);
            const request = store.add(imageData);

            request.onsuccess = () => {
                LogFirebase("OfflineMode", "indexed_DB_store_image", {
                    id: request.result,
                    sessionId: imageData.sessionId,
                });
                const { offlineModeUploadedImageCount } = reduxStore.getState().offlineMode;
                reduxStore.dispatch(setOfflineModeUploadedImageCount(offlineModeUploadedImageCount + 1));
                resolve(request.result);
            };

            request.onerror = (event) => {
                // Handle error and provide more context
                LogFirebase("OfflineMode", "indexed_DB_store_image_error", {
                    error: event?.target?.error?.message || "Unknown error",
                    sessionId: imageData.sessionId,
                });
                const error = request.error || event?.target?.error;
                reject(new Error("Failed to store image: " + (error?.message || "Unknown error")));
            };
        });
    }

    async deleteImagesFromOtherSessions(currentSessionId) {
        const db = await this.ensureDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.storeName, "readwrite");
            const store = transaction.objectStore(this.storeName);
            const index = store.index("sessionId");

            index.openCursor().onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    if (cursor.value.sessionId !== currentSessionId) {
                        LogFirebase("OfflineMode", "indexed_DB_delete_image_from_other_session", {
                            id: cursor?.primaryKey,
                            sessionId: cursor?.value?.sessionId,
                        });
                        store.delete(cursor.primaryKey);
                    }
                    cursor.continue();
                } else {
                    resolve();
                }
            };

            index.openCursor().onerror = () => {
                LogFirebase("OfflineMode", "indexed_DB_delete_image_from_other_session_error", {
                    sessionId: currentSessionId,
                });
                reject(new Error("Failed to delete images from other sessions"));
            };
        });
    }

    async getAllImages() {
        const db = await this.ensureDB();

        const transaction = db.transaction(this.storeName, "readonly");
        const store = transaction.objectStore(this.storeName);

        if (!store.indexNames.contains("sessionId")) {
            LogFirebase("OfflineMode", "indexed_DB_get_all_images_error", {
                error: "Index 'sessionId' does not exist",
            });
            throw new Error("Index 'sessionId' does not exist");
        }

        const index = store.index("sessionId");
        const sessionId = GLOBAL_MEETING_DATA.sessionId;

        const sessionImages = await new Promise((resolve, reject) => {
            const request = index.getAll(sessionId);

            request.onsuccess = (event) => {
                resolve(event.target.result);
            };

            request.onerror = (error) => {
                LogFirebase("OfflineMode", "indexed_DB_get_all_images_error", {
                    error: error?.target?.error?.message || "Unknown error",
                    sessionId,
                });
                reject(new Error("Failed to get images"));
            };
        });

        return sessionImages;
    }

    async deleteImage(id) {
        const db = await this.ensureDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.storeName, "readwrite");
            const store = transaction.objectStore(this.storeName);
            const request = store.delete(id);

            request.onsuccess = () => {
                LogFirebase("OfflineMode", "indexed_DB_delete_image", { id });
                resolve(id);
            };

            request.onerror = (error) => {
                LogFirebase("OfflineMode", "indexed_DB_delete_image_error", {
                    id,
                    error: error?.target?.error?.message || "Unknown error",
                });
                reject(new Error("Failed to delete image"));
            };
        });
    }

    async deleteAllImages() {
        const db = await this.ensureDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.storeName, "readwrite");
            const store = transaction.objectStore(this.storeName);
            const request = store.clear(); // This clears all entries

            request.onsuccess = () => {
                LogFirebase("OfflineMode", "indexed_DB_delete_all_images", "All images deleted successfully");
                resolve("All images deleted");
            };

            request.onerror = () => {
                LogFirebase("OfflineMode", "indexed_DB_delete_all_images_error", "Failed to delete all images");
                reject(new Error("Failed to delete all images"));
            };
        });
    }

    async getImageCount() {
        // not in use
        const db = await this.ensureDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.storeName, "readonly");
            const store = transaction.objectStore(this.storeName);
            const request = store.count();

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                reject(new Error("Failed to count images"));
            };
        });
    }
}

export const indexedDBService = new IndexedDBService();
